import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import { Table } from "components/ui/Table/Table";
import type {
  ContextMenuStatus,
  SelectContextMenuHandler,
} from "routes/emails/SearchEmailsPage";

type EmailReactTableProps<T> = {
  data: T[];
  // biome-ignore lint/suspicious/noExplicitAny: TanStack ColumnDef generic compatibility
  columns: ColumnDef<T, any>[];
  onSelectEmail: (id: string) => void;
  onSelectContextMenu: SelectContextMenuHandler;
  /** 詳細プレビューで選択中のメールID (行ハイライト用) */
  selectedId?: string | null;
};

const HIDDEN_COLUMN_ID = "emailId";

/**
 * メール検索結果テーブル。ui/Table primitives を使った業務固有ラッパー。
 * - `emailId` 列は描画対象から除外（内部的に行クリックで利用）
 * - `row.original.read` で muted トーンに切り替え
 * - 右クリックでコンテキストメニューを発火
 * @param {EmailReactTableProps} props テーブル props
 * @return {JSX.Element} テーブル要素
 */
export const EmailReactTable = <T extends Record<string, unknown>>({
  data,
  columns,
  onSelectEmail,
  onSelectContextMenu,
  selectedId = null,
}: EmailReactTableProps<T>): JSX.Element => {
  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Table.Root density="compact" zebra stickyHeader>
      <Table.Head sticky>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers
              .filter(header => header.id !== HIDDEN_COLUMN_ID)
              .map(header => {
                const canSort = header.column.getCanSort();
                return (
                  <Table.HeaderCell
                    key={header.id}
                    isSortable={canSort}
                    sortDirection={header.column.getIsSorted()}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </Table.HeaderCell>
                );
              })}
          </tr>
        ))}
      </Table.Head>
      <Table.Body>
        {table.getRowModel().rows.map((row: Row<T>) => {
          return (
            <Table.Row
              key={row.id}
              isClickable
              isSelected={row.getValue(HIDDEN_COLUMN_ID) === selectedId}
              onClick={() => {
                onSelectEmail(row.getValue(HIDDEN_COLUMN_ID));
              }}
              onContextMenu={event => {
                event.preventDefault();
                const status: ContextMenuStatus = {
                  id: row.getValue(HIDDEN_COLUMN_ID),
                  visible: true,
                  clientX: event.clientX,
                  clientY: event.clientY,
                };
                onSelectContextMenu(status);
              }}
            >
              {row
                .getVisibleCells()
                .filter(cell => cell.column.id !== HIDDEN_COLUMN_ID)
                .map(cell => (
                  <Table.Cell key={cell.id} nowrap>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table.Root>
  );
};
